import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { usePhaseFeature } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import ClientPortalPlaceholder from "../client/ClientPortalPlaceholder";

const money = (c) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Project({ pid }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api.get(`/phase6/portal/projects/${pid}`).then((r) => setP(r.data)).catch((e) => setError(errMsg(e, "Could not load."))); }, [pid]);
  if (error) return <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase6-portal-error">{error}</p>;
  if (!p) return null;
  return (
    <Card className="ds-card border-erf-borderCream" data-testid="phase6-portal-project">
      <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">{p.name}</h2>
        <p data-testid="phase6-portal-status">Status: {p.status_label}</p>
        {p.items.length === 0 && <p>Your project team has not shared any documents yet.</p>}
        <ul className="space-y-3">
          {p.items.map((i) => (
            <li key={i.id} className="rounded-lg border border-erf-borderCream p-3" data-testid={`phase6-portal-item-${i.id}`}>
              <div className="font-semibold">{i.title}</div>
              {i.kind === "pay_app" && <div>Period to {i.period_to} · amount due on this application {money(i.current_payment_due_cents)}</div>}
              {i.kind === "drawing" && <><div>Sheet {i.sheet_number || "—"} · status {i.status_letter} {i.status_name}</div><p className="mt-1 text-xs">{i.disclaimer}</p></>}
              {i.kind === "change_order" && <div>Approved change · {money(i.amount_cents)}{i.days_added ? ` · ${i.days_added} days added` : ""}</div>}
              {i.kind === "punch_item" && <div>Status: {i.state}{i.location ? ` · ${i.location}` : ""}</div>}
              {i.kind === "daily_log_summary" && <div>{i.log_date} · {i.total_headcount} workers on site{i.weather_summary ? ` · ${i.weather_summary}` : ""}</div>}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ClientPortal() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [pid, setPid] = useState(null);
  useEffect(() => { api.get("/phase6/portal/projects").then((r) => { setRows(r.data); setPid(r.data[0]?.id || null); }); }, []);
  return (
    <AdminAppShell activeNav="command" user={user} title="Your projects" subtitle="Only what your project team has shared with you.">
      <div data-testid="phase6-portal">
        {rows && rows.length === 0 && <p className="text-sm font-nav text-erf-dark" data-testid="phase6-portal-empty">You have not been given access to a project yet.</p>}
        <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Choose a project" data-testid="phase6-portal-picker">
          {(rows || []).map((p) => (
            <Button key={p.id} aria-pressed={pid === p.id} onClick={() => setPid(p.id)} data-testid={`phase6-portal-project-${p.id}`}
              variant={pid === p.id ? "default" : "outline"} className={pid === p.id ? "h-11 bg-erf-dark text-[#FEFCF7]" : "h-11 border-erf-borderCream text-erf-dark bg-white"}>{p.name}</Button>
          ))}
        </div>
        {pid && <Project key={pid} pid={pid} />}
      </div>
    </AdminAppShell>
  );
}

export default function ClientPortalEntry() {
  const { user } = useAuth();
  const f = usePhaseFeature(6, user?.id);
  if (!f) return <div className="p-8 text-sm font-nav text-erf-dark/70" data-testid="phase6-gate-loading">Loading…</div>;
  if (f.enabled && user?.role_key === "CLIENT") return <ClientPortal />;
  return <ClientPortalPlaceholder />;
}
